// Tesla-style two-column configurator.
//
//   ┌───────────────────────────────────────────────────────────┐
//   │ [LOGO]                            CONFIGURATOR / GAMMILL  │
//   ├───────────────────────────────────────┬───────────────────┤
//   │                                       │ 01 / MACHINE      │
//   │                                       │ ...               │
//   │              [HERO]                   │ 02 / UPGRADES     │
//   │   selected machine, swaps live        │ ...               │
//   │                                       │ 03 / ACCESSORIES  │
//   │   (left col sticky on desktop,        │ ...               │
//   │    collapses to top on mobile)        │ 04 / DETAILS      │
//   │                                       │ ...               │
//   │                                       │ 05 / SUMMARY      │
//   │                                       │ ...               │
//   │                                       │ [Generate Quote]  │
//   └───────────────────────────────────────┴───────────────────┘
//
// On submit success the right panel swaps to the Confirmation view; hero
// stays so the customer's machine is still on screen.

import { useState } from "react";
import { motion } from "framer-motion";

import { assetUrl } from "./format";
import { ConfiguratorProvider, useConfigurator } from "./store";
import { Hero } from "./components/Hero";
import { Confirmation } from "./components/Confirmation";
import { MachineSection } from "./components/sections/MachineSection";
import { TablesSection } from "./components/sections/TablesSection";
import { ExistingSystemSection } from "./components/sections/ExistingSystemSection";
import { AccessoriesSection } from "./components/sections/AccessoriesSection";
import { DetailsSection } from "./components/sections/DetailsSection";
import { SummarySection } from "./components/sections/SummarySection";
import { SubmitSection, MobileSubmitBar } from "./components/sections/SubmitSection";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { EmployeeModal } from "./components/EmployeeModal";
import { EmployeePanel } from "./components/EmployeePanel";
import { PromoBanner } from "./components/PromoBanner";
import { RunningEstimate } from "./components/RunningEstimate";
import { SiteNav } from "./components/SiteNav";

export default function App() {
  return (
    // Top-level boundary — catches crashes that escape section-level
    // boundaries (e.g. crashes inside ConfiguratorProvider, the Hero,
    // or the page header). Without this, an uncaught render error
    // unmounts the whole tree and the user sees a white screen with
    // no recovery affordance.
    <ErrorBoundary label="App">
      <ConfiguratorProvider>
        <Shell />
        {/* Mounted at the root so the hidden activation gestures can open it
            even while the catalog is still loading (Shell early-returns then). */}
        <EmployeeModal />
      </ConfiguratorProvider>
    </ErrorBoundary>
  );
}

function Shell() {
  const { state } = useConfigurator();
  // Mobile-only: let the customer collapse the sticky preview image so the
  // configuration form gets the full screen. Ignored on desktop, where the
  // hero is a side column.
  const [heroCollapsed, setHeroCollapsed] = useState(false);

  if (state.catalogLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center font-sans text-sm uppercase tracking-tag text-steel">
        Loading machines…
      </div>
    );
  }
  if (state.catalogError) {
    return (
      <div className="mx-auto flex max-w-md flex-col gap-3 px-4 py-16 text-center">
        <p className="font-display text-xl font-extrabold uppercase text-ink">
          Couldn't load the machines.
        </p>
        <p className="text-sm text-steel">
          {state.catalogError}. Please refresh or call us at 417-256-5919.
        </p>
      </div>
    );
  }
  if (!state.catalog) return null;

  return (
    // Mobile: the whole page scrolls naturally (no viewport lock), so the
    // config panel gets the full screen and tall-font / accessibility text
    // sizes don't get trapped in a cramped inner scroll box. Desktop keeps
    // the Tesla two-pane layout: fixed-height shell, each column scrolls on
    // its own.
    <div className="flex min-h-screen flex-col bg-white lg:h-screen lg:overflow-hidden">
      <Header />
      <div className="flex flex-1 flex-col lg:flex-row lg:overflow-hidden">
        {/* Hero — a compact sticky banner on mobile (stays in view so the
            live machine swap is visible while configuring, but takes a
            small, bounded slice of the screen), fills the left column on
            desktop. Height is viewport-based + capped so a bumped-up OS
            font can't blow it up. A mobile-only handle collapses the image
            so the form can take the full screen. */}
        {/* z-20 keeps the sticky hero above scrolling panel content — the
            option-row "i" badges are z-10 and would otherwise show through
            the hero as rows scroll up under it. Stays below modals (z-50). */}
        <div className="sticky top-0 z-20 flex-none border-b border-line bg-ink lg:static lg:h-auto lg:w-3/5 lg:flex-1 lg:border-b-0 lg:border-r">
          {/* Image area — collapses to 0 on mobile, always fills on desktop. */}
          <div
            className={
              "overflow-hidden transition-[height] duration-300 ease-out lg:h-full " +
              (heroCollapsed ? "h-0" : "h-[28vh] max-h-64 min-h-[150px]")
            }
          >
            <Hero />
          </div>
          {/* Collapse / expand handle — mobile only. */}
          <button
            type="button"
            onClick={() => setHeroCollapsed((v) => !v)}
            aria-expanded={!heroCollapsed}
            aria-label={heroCollapsed ? "Show machine preview" : "Hide machine preview"}
            className="flex w-full items-center justify-center gap-1.5 bg-ink py-1.5 font-sans text-[12px] font-bold uppercase tracking-tag text-white/60 transition hover:text-white lg:hidden"
          >
            {heroCollapsed ? "Show machine" : "Hide"}
            <svg
              viewBox="0 0 16 16"
              className={
                "h-3 w-3 transition-transform duration-300 " +
                (heroCollapsed ? "" : "rotate-180")
              }
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M3 6l5 5 5-5" />
            </svg>
          </button>
        </div>
        {/* Config panel — scrolls with the page on mobile, independently on desktop.
            Extra bottom padding on mobile (pb-36) clears the fixed MobileSubmitBar
            so the last section isn't trapped behind it; desktop has no bar (pb-10). */}
        <div className="flex flex-1 flex-col lg:w-2/5 lg:flex-none lg:overflow-hidden">
          <div className="flex flex-1 flex-col gap-2 px-4 pb-36 pt-2 sm:px-8 lg:overflow-y-auto lg:pb-6">
            {state.generated ? <Confirmation /> : <ConfigSections />}
          </div>
          {/* Desktop-only pinned footer — the promo banner, running estimate,
              and CTA stay at the bottom of the screen while the sections above
              scroll. Mobile uses the fixed MobileSubmitBar instead. */}
          {!state.generated && (
            <div className="hidden flex-none flex-col border-t border-line bg-white px-4 py-3 sm:px-8 lg:flex">
              <ErrorBoundary label="Promo"><PromoBanner /></ErrorBoundary>
              <ErrorBoundary label="Estimate"><RunningEstimate /></ErrorBoundary>
              <ErrorBoundary label="Submit"><SubmitSection /></ErrorBoundary>
            </div>
          )}
        </div>
      </div>
      {/* Mobile-only sticky CTA so Calculate / Email-me-the-quote is always in
          reach without scrolling to the end of the form. No-op on desktop. */}
      <MobileSubmitBar />
    </div>
  );
}

function ConfigSections() {
  const { state } = useConfigurator();
  // Pre-loved mode = a fixed used machine: hide Tables (it ships with its own
  // table) and Existing-System (upgrade-only), but KEEP Accessories so buyers
  // can add options to a pre-loved order (light bar, hydraulics, casters…).
  // The Machine section shows a read-only pre-loved card instead of the tabs.
  const preloved = Boolean(state.preloved);
  return (
    <>
      {state.employee.active && (
        <ErrorBoundary label="Employee"><EmployeePanel /></ErrorBoundary>
      )}
      {/* Promo banner: mobile shows it inline at the top; desktop shows it in
          the pinned footer (see the config column) along with the estimate. */}
      <div className="lg:hidden">
        <ErrorBoundary label="Promo"><PromoBanner /></ErrorBoundary>
      </div>
      <ErrorBoundary label="Machine"><MachineSection /></ErrorBoundary>
      {!preloved && (
        <>
          {/* Mutually exclusive — each returns null when not applicable. */}
          <ErrorBoundary label="Tables"><TablesSection /></ErrorBoundary>
          <ErrorBoundary label="Existing System"><ExistingSystemSection /></ErrorBoundary>
        </>
      )}
      {/* Accessories sell on pre-loved orders too — the section self-hides if
          none are visible for the machine's family. */}
      <ErrorBoundary label="Accessories"><AccessoriesSection /></ErrorBoundary>
      <ErrorBoundary label="Details"><DetailsSection /></ErrorBoundary>
      <ErrorBoundary label="Summary"><SummarySection /></ErrorBoundary>
    </>
  );
}

function Header() {
  const { state, registerSecretTap } = useConfigurator();
  return (
    <header className="border-b border-amber/40 bg-ink">
      <div className="mx-auto flex w-full items-center justify-between gap-4 px-6 py-4 sm:px-8">
        <motion.div
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
          className="flex items-center gap-4"
        >
          {/* Hidden activation: five quick taps on the logo opens the employee
              PIN modal. Looks/behaves like a plain logo otherwise. */}
          <img
            src={assetUrl("gammill-logo.png")}
            alt="Gammill"
            onClick={registerSecretTap}
            draggable={false}
            className="h-9 w-auto select-none"
            // Invert the black-on-transparent logo to render white on the
            // dark header — matches the tour's white-script-on-black bar.
            style={{ filter: "brightness(0) invert(1)" }}
          />
          <span aria-hidden className="hidden h-6 w-px bg-white/20 sm:block" />
          <div className="hidden items-center leading-tight sm:flex">
            <span className="font-display text-base font-extrabold text-white">
              Build a Machine
            </span>
          </div>
        </motion.div>
        <div className="flex items-center gap-2 sm:gap-3">
          {state.employee.active && (
            <span className="flex items-center gap-1.5 border border-amber/60 bg-amber/20 px-2.5 py-1 font-sans text-[12px] font-semibold uppercase tracking-tag text-amber">
              ⚡ {state.employee.repName ?? "Employee"}
            </span>
          )}
          <SiteNav />
        </div>
      </div>
    </header>
  );
}
